import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheck, Trash2 } from 'lucide-react'
import { authApi } from '@/lib/auth.api'
import { useAuthStore } from '@/lib/auth.store'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige, frage } from '@/lib/dialoge'
import { fmtDateLong } from '@/lib/format-date'
import { ZweiFaktorEinrichtung } from '@/components/zwei-faktor/ZweiFaktorEinrichtung'
import type { RegistriertesGeraet, ZweiFaktorStatus } from '@/lib/auth.types'

type Stand = ZweiFaktorStatus & { geraete: RegistriertesGeraet[] }

const knopf =
  'rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground hover:bg-muted disabled:opacity-50'

/**
 * AP-38-E — Die eigene Zwei-Faktor-Anmeldung im Profil: Stand, einrichten,
 * Verfahren wechseln, neue Wiederherstellungscodes, gemerkte Geräte.
 *
 * Alles hier sind Handlungen (AP-36 Fall 3) — Knöpfe mit Verb, keine Felder.
 * Wer schon eingerichtet hat, bestätigt Wechsel und neue Codes mit einem
 * gültigen Code: Ein offen gelassener Browser soll den zweiten Faktor nicht
 * gegen den eines Fremden tauschen können.
 */
export function ZweiFaktorKarte() {
  const { t } = useTranslation()
  const { loadUser } = useAuthStore()
  const [stand, setStand] = useState<Stand | null>(null)
  const [einrichten, setEinrichten] = useState<{ code?: string } | null>(null)
  const [neueCodes, setNeueCodes] = useState<string[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const anker = useRef<HTMLElement | null>(null)

  const laden = useCallback(async () => {
    try {
      setStand((await authApi.twoFactor()).data.data)
    } catch (e) {
      setFehler(apiErrorText(e, t('common.error')))
    }
  }, [t])

  useEffect(() => {
    void laden()
  }, [laden])

  // Der Hinweis „Jetzt einrichten" führt mit `#zwei-faktor` hierher.
  useEffect(() => {
    if (window.location.hash === '#zwei-faktor' && stand) anker.current?.scrollIntoView({ block: 'start' })
  }, [stand])

  /** Ein gültiger Code zur Bestätigung — bei E-Mail wird er vorher verschickt. */
  const holeCode = async (): Promise<string | null> => {
    if (stand?.verfahren === 'EMAIL') {
      try {
        await authApi.sendOwnCode()
      } catch (e) {
        setFehler(apiErrorText(e, t('common.error')))
        return null
      }
    }
    const text = stand?.verfahren === 'EMAIL' ? t('zweiFaktor.mailCodeFuerAenderung') : t('zweiFaktor.codeFuerAenderung')
    return frage(text, '', { pflicht: true, ok: t('zweiFaktor.weiter') })
  }

  const wechseln = async () => {
    setFehler(null)
    const code = await holeCode()
    if (code) setEinrichten({ code: code.trim() })
  }

  const codesErneuern = async () => {
    setFehler(null)
    const code = await holeCode()
    if (!code) return
    try {
      setNeueCodes((await authApi.newRecoveryCodes(code.trim())).data.data.recoveryCodes)
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, t('auth.twoFactorCodeInvalid')))
    }
  }

  const vergessen = async (g: RegistriertesGeraet) => {
    if (!(await bestaetige(`${g.label ?? t('zweiFaktor.unbekanntesGeraet')}: ${t('zweiFaktor.vergessen')}?`, { ok: t('zweiFaktor.vergessen'), gefaehrlich: true }))) return
    try {
      await authApi.forgetDevice(g.id)
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, t('common.error')))
    }
  }

  return (
    <section id="zwei-faktor" ref={anker} className="scroll-mt-4 rounded-lg border bg-card p-6 shadow-sm">
      <h2 className="mb-4 flex items-center gap-2 text-base font-semibold text-foreground">
        <ShieldCheck size={18} className="text-muted-foreground" />
        {t('zweiFaktor.titel')}
      </h2>

      {fehler && (
        <p className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>
      )}

      {stand && !einrichten && !neueCodes && (
        <div className="space-y-4 text-sm">
          <p className="text-foreground">
            {stand.eingerichtet
              ? stand.verfahren === 'EMAIL'
                ? t('zweiFaktor.eingerichtetEmail')
                : t('zweiFaktor.eingerichtetApp')
              : t('zweiFaktor.nichtEingerichtet')}
            {!stand.eingerichtet && stand.pflichtAb && (
              <span className="ml-2 text-amber-700 dark:text-amber-400">
                {t('zweiFaktor.pflichtAb', { datum: fmtDateLong(stand.pflichtAb) })}
              </span>
            )}
          </p>
          {stand.eingerichtet && (
            <p className="text-muted-foreground">{t('zweiFaktor.restCodes', { anzahl: stand.restlicheCodes })}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {!stand.eingerichtet ? (
              <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90" onClick={() => setEinrichten({})}>
                {t('zweiFaktor.einrichten')}
              </button>
            ) : (
              <>
                <button type="button" className={knopf} onClick={() => void wechseln()}>
                  {t('zweiFaktor.verfahrenWechseln')}
                </button>
                <button type="button" className={knopf} onClick={() => void codesErneuern()}>
                  {t('zweiFaktor.neueCodes')}
                </button>
              </>
            )}
          </div>

          <div>
            <h3 className="mb-2 font-medium text-foreground">{t('zweiFaktor.geraete')}</h3>
            {stand.geraete.length === 0 ? (
              <p className="text-muted-foreground">{t('zweiFaktor.keineGeraete')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {stand.geraete.map((g) => (
                  <li key={g.id} className="flex items-center justify-between gap-3 py-2">
                    <span>
                      <span className="text-foreground">{g.label ?? t('zweiFaktor.unbekanntesGeraet')}</span>
                      <span className="ml-2 text-xs text-muted-foreground">
                        {t('zweiFaktor.geraetBis', { datum: fmtDateLong(g.expiresAt) })}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      onClick={() => void vergessen(g)}
                      aria-label={t('zweiFaktor.vergessen')}
                      title={t('zweiFaktor.vergessen')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {einrichten && (
        <ZweiFaktorEinrichtung
          starte={async (verfahren) => (await authApi.startOwnSetup(verfahren, einrichten.code)).data.data}
          bestaetige={async (code) => (await authApi.confirmOwnSetup(code)).data.data.recoveryCodes}
          onFertig={async () => {
            setEinrichten(null)
            await laden()
            // Der Hinweis in der Leiste liest den Benutzer.
            await loadUser()
          }}
          onAbbrechen={() => setEinrichten(null)}
        />
      )}

      {neueCodes && (
        <ZweiFaktorEinrichtungCodes codes={neueCodes} onFertig={() => setNeueCodes(null)} />
      )}
    </section>
  )
}

/** Neue Wiederherstellungscodes anzeigen — derselbe Abschluss wie nach der Einrichtung. */
function ZweiFaktorEinrichtungCodes({ codes, onFertig }: { codes: string[]; onFertig: () => void }) {
  const { t } = useTranslation()
  const [gesichert, setGesichert] = useState(false)
  return (
    <div className="space-y-4 text-sm">
      <p className="font-medium text-foreground">{t('zweiFaktor.codesTitel')}</p>
      <p className="text-muted-foreground">{t('zweiFaktor.codesHinweis')}</p>
      <ul className="grid grid-cols-2 gap-2 rounded-md border bg-muted/40 p-3 font-mono">
        {codes.map((c) => (
          <li key={c} className="text-foreground">
            {c}
          </li>
        ))}
      </ul>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={gesichert} onChange={(e) => setGesichert(e.target.checked)} />
        <span className="text-foreground">{t('zweiFaktor.codesGesichert')}</span>
      </label>
      <div className="flex justify-end">
        <button type="button" disabled={!gesichert} onClick={onFertig} className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          {t('zweiFaktor.fertig')}
        </button>
      </div>
    </div>
  )
}
